import { PlusIcon } from "lucide-react";
import {
  useGetDonationsCampaignListQuery,
  useGetDonationsLedgerQuery,
  useListDonationsPendingDedicationsQuery,
} from "../../../state/domainRouterApi";
import { ModuleSectionNav } from "../../../components/navigation/ModuleSectionNav";
import { YstRouteTransition } from "../../../components/motion/YstRouteTransition";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { DONATIONS_SECTION_ITEMS } from "./sectionNav";

const PRIMARY = "var(--primary)";
const SUCCESS = "var(--aquamarine)";
const TEXT = "var(--iron-grey)";
const BORDER = "var(--border)";

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((index) => (
        <div key={index} className="h-20 animate-pulse rounded-xl bg-muted" />
      ))}
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return <SupportErrorMessage>{message}</SupportErrorMessage>;
}

type CampaignRow = {
  id?: string;
  name?: string;
  campaignType?: string;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
};

function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return "$0";
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const humanizeCampaignType = (value: string | undefined): string => {
  switch (value) {
    case "annual-fund":
      return "Annual Fund";
    case "parnes-hayom":
      return "Parnes HaYom";
    case "general":
      return "General Donations";
    default:
      return value ?? "--";
  }
};

function CampaignTable({
  campaigns,
  raisedByCampaign,
}: {
  campaigns: CampaignRow[];
  raisedByCampaign: Map<string, number>;
}) {
  if (campaigns.length === 0) {
    return (
      <div className="py-12 text-center text-sm" style={{ color: TEXT }}>
        No campaigns found. Create your first campaign to get started.
      </div>
    );
  }

  return (
    <div className="yst-surface-panel overflow-hidden">
      <div className="source-faithful-tableScroll">
        <table
          className="min-w-[760px] divide-y text-sm"
          style={{ "--tw-divide-color": BORDER } as React.CSSProperties}
        >
          <thead>
            <tr className="yst-surface-table-head">
              <th
                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                style={{ color: TEXT }}
              >
                Campaign
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                style={{ color: TEXT }}
              >
                Type
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                style={{ color: TEXT }}
              >
                Status
              </th>
              <th
                className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider"
                style={{ color: TEXT }}
              >
                Raised
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                style={{ color: TEXT }}
              >
                Dates
              </th>
            </tr>
          </thead>
          <tbody
            className="divide-y bg-white"
            style={{ "--tw-divide-color": BORDER } as React.CSSProperties}
          >
            {campaigns.map((campaign) => (
              <tr
                key={campaign.id ?? campaign.name}
                className="yst-motion-color hover:bg-muted/70"
              >
                <td className="px-6 py-4 font-medium" style={{ color: TEXT }}>
                  <a
                    href={`/school/donations/campaigns/${campaign.id ?? ""}`}
                    className="hover:underline"
                    style={{ color: PRIMARY }}
                  >
                    {campaign.name ?? "Untitled"}
                  </a>
                </td>
                <td className="px-6 py-4" style={{ color: TEXT }}>
                  {humanizeCampaignType(campaign.campaignType)}
                </td>
                <td className="px-6 py-4">
                  <span
                    className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold"
                    style={
                      campaign.isActive
                        ? { backgroundColor: `${SUCCESS}22`, color: "#065f46" }
                        : { backgroundColor: "#f3f4f6", color: TEXT }
                    }
                  >
                    {campaign.isActive ? "active" : "inactive"}
                  </span>
                </td>
                <td
                  className="px-6 py-4 text-right font-medium"
                  style={{ color: TEXT }}
                >
                  {formatCurrency(raisedByCampaign.get(campaign.id ?? "") ?? 0)}
                </td>
                <td className="px-6 py-4 text-sm" style={{ color: TEXT }}>
                  {campaign.startDate ?? "--"} &ndash;{" "}
                  {campaign.endDate ?? "--"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SchoolDonationsDashboardPage() {
  const { data, isLoading, isError, error } = useGetDonationsCampaignListQuery(
    {},
  );
  const { data: ledgerData } = useGetDonationsLedgerQuery({});
  const { data: pendingData } = useListDonationsPendingDedicationsQuery({});

  const campaigns: CampaignRow[] = (() => {
    if (!data) return [];
    const candidate = data as Record<string, unknown>;
    if (Array.isArray(candidate.items)) return candidate.items as CampaignRow[];
    return [];
  })();

  const raisedByCampaign = (() => {
    const map = new Map<string, number>();
    const candidate = ledgerData as Record<string, unknown> | undefined;
    const items = Array.isArray(candidate?.items)
      ? (candidate.items as Array<Record<string, unknown>>)
      : [];
    for (const item of items) {
      const campaignId =
        typeof item.campaignId === "string" ? item.campaignId : "";
      if (!campaignId) continue;
      const amount =
        Number(item.grossCents ?? 0) + Number(item.feeRecoveryCents ?? 0);
      map.set(
        campaignId,
        (map.get(campaignId) ?? 0) + (Number.isFinite(amount) ? amount : 0),
      );
    }
    return map;
  })();

  const totalCampaigns = campaigns.length;
  const totalRaisedCents = [...raisedByCampaign.values()].reduce(
    (sum, value) => sum + value,
    0,
  );
  const pendingDedications = Array.isArray(
    (pendingData as Record<string, unknown> | undefined)?.items,
  )
    ? ((pendingData as Record<string, unknown>).items as unknown[]).length
    : 0;

  const stats = [
    { label: "Total campaigns", value: String(totalCampaigns) },
    { label: "Total raised", value: formatCurrency(totalRaisedCents) },
    { label: "Pending dedications", value: String(pendingDedications) },
  ];

  return (
    <main
      data-testid="school-donations-dashboard"
      data-route-ready="true"
      data-extracted-from="source-faithful:school-donations-v1.0"
      className="source-faithful-shell"
      style={
        { "--ystnj-source-faithful-max-width": "1120px" } as React.CSSProperties
      }
    >
      <YstRouteTransition className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="yst-surface-title">Campaign Dashboard</h2>
          </div>
          <a
            href="/school/donations/campaigns/new"
            className="yst-button-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold"
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            New Campaign
          </a>
        </div>

        <ModuleSectionNav
          items={DONATIONS_SECTION_ITEMS}
          activeId="dashboard"
          ariaLabel="Donations sections"
          desktopClassName="source-faithful-tabs"
        />

        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <ErrorBanner
            message={
              (error && typeof error === "object" && "message" in error
                ? String((error as { message?: unknown }).message)
                : null) ??
              "Campaign data is not yet available. The donations backend has not been deployed."
            }
          />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              {stats.map((stat) => (
                <div key={stat.label} className="yst-surface-panel p-5">
                  <p
                    className="mb-2 text-[12px] font-semibold uppercase tracking-[0.05em]"
                    style={{ color: `${TEXT}99` }}
                  >
                    {stat.label}
                  </p>
                  <p
                    className="text-[28px] font-semibold leading-tight"
                    style={{ color: TEXT }}
                  >
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>

            <CampaignTable
              campaigns={campaigns}
              raisedByCampaign={raisedByCampaign}
            />
          </>
        )}
      </YstRouteTransition>
    </main>
  );
}
